//done
//@ts-check
import Alpine from 'alpinejs';
import * as config from "../constants.js";
import {} from "./util.js";
import { updateUI } from "./game.js";
import { createContractorCard, getAllAvailableContractors, toggleContractor } from "./contractor.js"
import { getContractorSynergyStatus } from "./contractor-core.js";
import { isSynergyRevealed } from "./character-core.js";
import { gameState, saveGameState } from "./gameState.js";
import { addNotification } from "./notifications.js";
import { multiplayer } from "./multiplayer.js";
import { getTeamSkillLevels, getPathRequiredSkills } from "./raid.js";
import { getRandomAvatar, getSkillIcon } from "./util.js";

export function createAlpineContractorStore() {
    // Alpine.js Contractor Store
    Alpine.store('contractors', {
        list: [],
        reserved: [],
        hired: [],

        init() {
            // Initialize with current gameState data
            this.refresh();
        },

        async refresh() {
            // Update store from gameState (AI contractors) + marketplace contractors
            this.list = await getAllAvailableContractors();

            // console.log('Contractors refreshed, total contractors:', this.list.length, this.list);

            this.reserved = gameState.currentRaid.reservedContractors || [];
            this.hired = gameState.currentRaid.hiredContractors || [];

            // Update status on all contractor objects
            this.updateContractorStatuses();
        },

        async update(newContractors) {
            // Update both gameState and Alpine store
            gameState.contractors = newContractors;
            await this.refresh();
        },

        updateReserved(newReserved) {
            // Update both gameState and Alpine store
            // console.log('Store updateReserved called with:', newReserved);
            gameState.currentRaid.reservedContractors = newReserved;
            this.reserved = newReserved;
            // console.log('Store reserved array updated to:', this.reserved);

            // Update status on all contractor objects
            this.updateContractorStatuses();
        },

        updateHired(newHired) {
            // Update both gameState and Alpine store
            gameState.currentRaid.hiredContractors = newHired;
            this.hired = newHired;

            // Update status on all contractor objects
            this.updateContractorStatuses();
        },

        updateContractorStatuses() {
            // Create copies of contractors with status field for the store only
            const reservedIds = this.reserved.map(c => c.id);
            const hiredIds = this.hired.map(c => c.id);

            // Get current path skills and team skills for relevance calculation
            const pathSkills = getPathRequiredSkills();
            const teamSkills = getTeamSkillLevels();

            this.list = this.list.map(contractor => {
                const contractorCopy = { ...contractor };

                // Calculate status
                if (contractor.injuredUntil && contractor.injuredUntil > Date.now()) {
                    contractorCopy.status = 'injured';
                } else if (hiredIds.includes(contractor.id)) {
                    contractorCopy.status = 'hired';
                } else if (reservedIds.includes(contractor.id)) {
                    contractorCopy.status = 'reserved';
                } else {
                    contractorCopy.status = 'available';
                }

                // Calculate skill relevance indicators
                const contractorSkills = Object.keys(contractor.skills || {});
                const totalNeededSkills = contractorSkills.filter(skill => pathSkills.has(skill)).length;
                const uncoveredNeededSkills = contractorSkills.filter(skill =>
                    pathSkills.has(skill) && (teamSkills[skill] || 0) === 0
                ).length;

                let maxLevelNeededSkill = 0;
                contractorSkills.filter(skill =>
                    pathSkills.has(skill) && (teamSkills[skill] || 0) === 0
                ).forEach(skill => {
                    if (contractor.skills[skill] > maxLevelNeededSkill) {
                        maxLevelNeededSkill = contractor.skills[skill];
                    }
                });

                // Add relevance properties to contractor copy
                contractorCopy.totalNeededSkills = totalNeededSkills;
                contractorCopy.uncoveredNeededSkills = uncoveredNeededSkills;
                contractorCopy.hasNeededSkills = totalNeededSkills > 0;
                contractorCopy.hasUncoveredSkills = uncoveredNeededSkills > 0;
                contractorCopy.maxLevelNeededSkill = maxLevelNeededSkill;

                // Synergy relevance: ready (partner gear equipped on the
                // prospective team, or their gear partners your skills) vs
                // swap-possible (player owns an unequipped partner device)
                const synergy = getContractorSynergyStatus(contractor);
                contractorCopy.hasSynergyReady = synergy.hasReady;
                contractorCopy.hasSynergySwap = synergy.hasSwap;

                return contractorCopy;
            });
        },

        get all() {
            return this.list;
        },

        get allReserved() {
            return this.reserved;
        },

        get allHired() {
            return this.hired;
        },

        get count() {
            return this.list.length;
        },

        get reservedIds() {
            return this.reserved.map(c => c.id);
        },

        get hiredIds() {
            return this.hired.map(c => c.id);
        }
    });
};

// Alpine.js Contractor Manager Component
export function contractorManager() {
    return {
        searchTerm: '',
        selectedFaction: '',
        selectedStatus: '',
        synergyFilter: '', // '' = all, 'ready' = ⚡ synergy ready, 'possible' = ready or one swap away

        get contractors() {
            // Use the Alpine store instead of direct gameState access
            return this.$store.contractors.all;
        },

        get synergyRevealed() {
            // The synergy filter only exists once the layer is revealed
            return isSynergyRevealed();
        },

        get reservedContractors() {
            // Use the Alpine store instead of direct gameState access
            return this.$store.contractors.allReserved;
        },

        get hiredContractors() {
            // Use the Alpine store instead of direct gameState access
            return this.$store.contractors.allHired;
        },

        get filteredContractors() {
            let filtered = this.contractors;
            let hired = this.hiredContractors;
            let reserved = this.reservedContractors;

            // Search filter (name and skills)
            if (this.searchTerm) {
                const term = this.searchTerm.toLowerCase();
                filtered = filtered.filter(contractor => {
                    // Search name
                    if (contractor.name.toLowerCase().includes(term)) return true;

                    // Search skills
                    const skillNames = Object.entries(contractor.skills || {}).map(([key, level]) => {
                        const skillInfo = this.getSkillInfo(key, contractor.faction);
                        return skillInfo ? skillInfo.name : key;
                    }).join(' ').toLowerCase();

                    return skillNames.includes(term);
                });
            }

            // Faction filter
            if (this.selectedFaction) {
                filtered = filtered.filter(contractor => contractor.faction === this.selectedFaction);
            }

            // Status filter
            if (this.selectedStatus) {
                filtered = filtered.filter(contractor => {
                    const status = this.getContractorStatus(contractor);
                    return status === this.selectedStatus;
                });
            }

            // Synergy filter: 'ready' = partner gear already on the team (or
            // their gear partners your skills); 'possible' also counts
            // contractors one player gear swap away
            if (this.synergyFilter === 'ready') {
                filtered = filtered.filter(c => c.hasSynergyReady);
            } else if (this.synergyFilter === 'possible') {
                filtered = filtered.filter(c => c.hasSynergyReady || c.hasSynergySwap);
            }

            // console.log('Contractors filtered, total contractors:', filtered.length, filtered);

            return filtered;
        },

        get modalSortedContractors() {
            const reservedCount = this.$store.contractors.reserved.length;
            // console.log('modalSortedContractors called, reserved count:', reservedCount);

            // Filter to only available contractors for modal
            let available = this.filteredContractors.filter(contractor => {
                const isAvailable = contractor.status === 'available';
                const teamNotFull = reservedCount < 2;
                //console.log(`Contractor ${contractor.name}: status=${contractor.status}, teamNotFull=${teamNotFull}`);
                return isAvailable && teamNotFull;
            });

            // Sort by relevance using pre-calculated properties
            return available.sort((a, b) => {
                // Primary sort: contractors with uncovered needed skills first
                if (a.uncoveredNeededSkills !== b.uncoveredNeededSkills) {
                    return b.uncoveredNeededSkills - a.uncoveredNeededSkills;
                }
                
                // Secondary sort: level of uncovered needed skills
                if (a.maxLevelNeededSkill !== b.maxLevelNeededSkill) {
                    return b.maxLevelNeededSkill - a.maxLevelNeededSkill;
                }

                // Tertiary sort: contractors with any needed skills
                if (a.totalNeededSkills !== b.totalNeededSkills) {
                    return b.totalNeededSkills - a.totalNeededSkills;
                }

                // Quaternary sort: contractors with higher reward percentage
                return a.rewardPercentage - b.rewardPercentage;
            });
        },


        getContractorStatus(contractor) {
            // Return the pre-calculated status field
            return contractor.status || 'available';
        },

        getSkillInfo(skillKey, faction) {
            return config.factionSkills[faction]?.skills[skillKey] || null;
        },

        createContractorCardHTML(contractor) {
            return createContractorCard(contractor, 'main');
        },

        clearFilters() {
            this.searchTerm = '';
            this.selectedFaction = '';
            this.selectedStatus = '';
            this.synergyFilter = '';
        },

        // Force refresh contractors from gameState
        refreshContractors() {
            // Trigger reactivity by accessing the getter
            this.contractors;
            this.$nextTick();
        },


        // Refresh contractors when the component is initialized or updated
        init() {
            // Initial refresh
            this.refreshContractors();

            // Make sure store is initialized with current data
            this.$store.contractors.refresh();
            // console.log('Initial store state - reserved:', this.$store.contractors.reserved);

            // Watch for game state changes and refresh
            this.$watch('contractors', () => {
                this.$nextTick(() => {
                    // Ensure event handlers are attached to new elements
                    attachContractorEventHandlers();
                });
            });

            // Watch for reserved contractors changes in the store
            this.$watch('$store.contractors.reserved', (newValue, oldValue) => {
                // console.log('Reserved contractors changed in store from', oldValue, 'to', newValue);
                this.$store.contractors.updateContractorStatuses();
                this.$nextTick();
            });

            // Watch for hired contractors changes in the store
            this.$watch('$store.contractors.hired', (newValue, oldValue) => {
                // console.log('Hired contractors changed in store from', oldValue, 'to', newValue);
                this.$store.contractors.updateContractorStatuses();
                this.$nextTick();
            });
        }
    }
}

// Helper export function to reattach event handlers to contractor cards
export function attachContractorEventHandlers() {
    // Reattach click handlers for contractor buttons
    document.querySelectorAll('.contractor-card .hire-btn').forEach(btn => {
        const contractorId = btn.getAttribute('data-contractor-id');
        if (contractorId) {
            btn.onclick = () => toggleContractor(contractorId);
        }
    });
}

// Global export function to refresh contractors in Alpine store
export function refreshAlpineContractors() {
    // Update the Alpine store with current gameState data
    if (Alpine.store('contractors')) {
        Alpine.store('contractors').refresh();
    }
};